/*
 * Lazy load background images using IntersectionObserver
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 * Usage: Add the class "lazyload-background" to the element and set the data-bgimage attribute to the image URL
 * <div class="lazyload-background" data-bgimage="image.jpg"></div>
 */
(function() {
  // Wait for DOM to be ready, works with both jQuery and vanilla JS
  function domReady(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else if (document.addEventListener) {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      document.attachEvent('onreadystatechange', function() {
        if (document.readyState !== 'loading') fn();
      });
    }
  }

  domReady(function() {
    const lazyBackgroundElements = document.querySelectorAll('.lazyload-background');
    
    if (!lazyBackgroundElements.length) return;

    // Function to load background image with error handling
    function loadBackgroundImage(element) {
      try {
        const bgImage = element.dataset.bgimage;
        
        if (!bgImage) return false;
        
        const img = new Image();
        
        img.onload = function() {
          element.style.backgroundImage = `url('${bgImage}')`;
          element.classList.add('mad_background-loaded');
        };
        
        img.onerror = function() {
          console.warn(`Failed to load background image: ${bgImage}`);
          element.classList.add('mad_background-load-error');
        };
        
        img.src = bgImage;
        return true;
      } catch (error) {
        console.error('Error loading background image:', error);
        return false;
      }
    }

    if ('IntersectionObserver' in window) {
      try {
        // Use WeakSet to track observed elements without preventing garbage collection
        const observedElements = new WeakSet();
        
        const observer = new IntersectionObserver(
          function(entries) {
            entries.forEach(function(entry) {
              if (entry.isIntersecting) {
                const success = loadBackgroundImage(entry.target);
                if (success) {
                  observer.unobserve(entry.target);
                  observedElements.delete(entry.target);
                }
              }
            });
          },
          { 
            rootMargin: '150px',
            threshold: 0.01
          }
        );
    
        // Observe all elements with the .lazyload-background class
        lazyBackgroundElements.forEach(function(element) {
          if (element.dataset.bgimage) {
            observer.observe(element);
            observedElements.add(element);
          }
        });
    
        // Delayed loading for anything missed during scroll
        const idleLoadImages = function() {
          lazyBackgroundElements.forEach(function(element) {
            if (element.dataset.bgimage && 
                !element.classList.contains('mad_background-loaded') && 
                !element.classList.contains('mad_background-load-error') &&
                observedElements.has(element)) {
              loadBackgroundImage(element);
              observer.unobserve(element);
              observedElements.delete(element);
            }
          });
        };

        // Use requestIdleCallback or setTimeout as fallback
        if ('requestIdleCallback' in window) {
          requestIdleCallback(idleLoadImages, { timeout: 2000 });
        } else {
          setTimeout(idleLoadImages, 3000);
        }
      } catch (error) {
        console.error('Error setting up IntersectionObserver:', error);
        lazyBackgroundElements.forEach(loadBackgroundImage);
      }
    } else {
      // Fallback for browsers without IntersectionObserver support
      let scrolling = false;
      
      function lazyLoadBackgrounds() {
        if (scrolling) return;
        
        scrolling = true;
        setTimeout(function() {
          lazyBackgroundElements.forEach(function(element) {
            // Simple check if element is in viewport
            const rect = element.getBoundingClientRect();
            const inViewport = (
              rect.top <= (window.innerHeight || document.documentElement.clientHeight) + 150 &&
              rect.bottom >= 0
            );
            
            if (inViewport && element.dataset.bgimage && 
                !element.classList.contains('mad_background-loaded') && 
                !element.classList.contains('mad_background-load-error')) {
              loadBackgroundImage(element);
            }
          });
          scrolling = false;
        }, 100);
      }
      
      // Load visible images immediately
      lazyLoadBackgrounds();
      
      // Add event listeners for scroll with passive option for performance
      window.addEventListener('scroll', lazyLoadBackgrounds, { passive: true });
      window.addEventListener('resize', lazyLoadBackgrounds, { passive: true });
      window.addEventListener('orientationchange', lazyLoadBackgrounds, { passive: true });
    }
  });
})();